<template>
  <div class="gallery-container">
    <div v-if="isAuthenticated" class="go-back" @click="$router.go(-1)"><i class="fa fa-chevron-left"/></div>

    <div class="gallery-title">{{ projectName }}</div>
    
    <div v-if="!isAuthenticated && photos.length < 1" class="secret-container">
      <check-secret :user="user" :project-name="projectName"  @unlock="unlock" />
    </div>

    <template v-else>
      <div class="controls">
        <div class="highlight-favourites" @click="toggleHighlightFavourites()">
          <i v-if="highlightFavourites" class="fa fa-heart red"></i>
          <i v-else class="far fa-heart"></i>
        </div>
        <div class="slideshow-control" @click="slideshow('start')">
          <i class="fa fa-tv"/>
        </div>
      </div>
      <div v-if="loading">
        <img
          src="../../assets/spinner.svg"
          alt="loading"
        >
      </div>

      <div v-if="photos.length < 1 && !loading">No photos uploaded yet.</div>

      <div class="custom-masonry">
        <div class="column" v-for="c in masonryColumns" :key="c">
          <transition-group name="slide-fade" appear>
            <gallery-tile v-for="photo in masonryOrderedItems[c-1]" 
              :photo="photo" 
              :key="photo.url" 
              :highlight-favourites="highlightFavourites"
              @toggle-favourite="toggleFavourite"
              @slideshow="slideshow(photo.url)"/>
          </transition-group>
        </div>
      </div>

      <transition name="fade" :duration="100">
        <slideshow 
          v-show="showSlideshow" 
          :slides="photos" 
          :start-index.sync="slideIndex"
          @toggle-favourite="toggleFavourite"
          @close="showSlideshow = false"/>
      </transition>

      <exit-intent v-if="false">
        <div class="selection-complete-message">
          Do you want to continue selecting your favourites later, or are you done and {{ user }} should be notified of your favourites selection?
          <div class="buttons">
            <div class="button continue">Continue Later</div>
            <div class="button complete">
              I'm done <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="feather feather-check-circle"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><polyline points="22 4 12 14.01 9 11.01"></polyline></svg>
            </div>
          </div>
        </div>
      </exit-intent>
    </template>
  </div>
</template>

<script>
import accessURLService from '@/services/access-url-service.js'
import projectService from '@/services/project-service.js'
import { deURLify } from '@/utils/urlify.js'

import CheckSecret from '@/components/utility/CheckSecret.vue'
import ExitIntent from '@/components/ExitIntent.vue'
import GalleryTile from '@/components/gallery/GalleryTile.vue'
import Slideshow from '@/components/gallery/Slideshow.vue'

export default {
  name: 'Gallery',
  components: {
    CheckSecret,
    ExitIntent,
    GalleryTile,
    Slideshow,
  },
  props: {
    user: {
      type: String,
      required: true,
    },
    project: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      photos: [],

      highlightFavourites: false,
      screenWidth: 0,

      showSlideshow: false,
      slideIndex: 0,

      loading: false,
      err: null,
    }
  },
  created() {
    window.addEventListener('resize', this.handleResize)
    window.addEventListener('keydown', this.closeSlideshow)
    this.handleResize()
    // this.loadAccessUrlsAndFavourites()
  },
  mounted() {
    if(this.isAuthenticated) {
      this.loadAccessUrlsAndFavourites('')
    }
  },
  destroyed() {
    window.removeEventListener('resize', this.handleResize)
    window.removeEventListener('keydown', this.closeSlideshow)
  },
  methods: {
    unlock(secret) {
      this.loadAccessUrlsAndFavourites(secret)
    },
    async loadAccessUrlsAndFavourites(secret) {
      this.loading = true

      var promises = []
      promises.push(accessURLService.getGalleryAccessURLs(this.user, this.projectName, secret))
      promises.push(projectService.getFavourites(this.user, this.projectName))
      Promise.all(promises)
        .then((res) => {
          let urls = res[0]
          let favourites = res[1]
          
          let photos = urls.map(u => { 
            return Object.assign(u, { favourite: favourites.includes(u.name) })
          })
          photos.sort((a, b) => a.name > b.name ? 1 : -1)
          this.photos = photos
          this.loading = false
        }).catch((err) => { // FIXME: this doesn't work yet
          // eslint-disable-next-line no-console
            console.dir(err)
            this.err = err
            this.loading = false
        });
    },
    slideshow(url) {
      if(url == 'start') {
        if(this.photos.length <= 0) {
          return
        }
        url = this.photos[0].url
      }
      let i = this.photos.findIndex(e => e.url == url)
      this.slideIndex = i
      this.showSlideshow = true
    },
    handleResize() {
      this.screenWidth = window.innerWidth;
    },
    toggleHighlightFavourites() {
      this.highlightFavourites = !this.highlightFavourites
    },
    toggleFavourite(p) {
      let i = this.photos.findIndex(e => e.url == p.url)
      this.photos[i].favourite = !this.photos[i].favourite

      let favourites = this.photos.filter(i => i.favourite).map(i => i.name)
      projectService.markFavourites(this.user, this.projectName, favourites)
        .catch(err => this.err = err)
        // .then(r => console.log("marked favourites: " + r + " -> " + favourites))
    },
    closeSlideshow(e) {
      if(e.key == 'Escape')
        this.showSlideshow = false
    }
  },
  computed: {
    projectName() {
      return deURLify(this.project)
    },
    isAuthenticated() {
      return this.$store.getters.isAuthenticated
    },
    masonryColumns() {
      if(this.screenWidth > 1200) {
        return 4
      }
      if(this.screenWidth > 768) {
        return 3
      }
      return 2
    },
    masonryOrderedItems() {
      let out = []
      for(let i = 0; i < this.masonryColumns; i++) {
        out[i] = []
        let j = i
        while(j < this.photos.length) {
          out[i].push(this.photos[j])
          j += this.masonryColumns
        }
      }
      return out
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/assets/shared.scss';
.gallery-container {
  padding: 0px 40px;
}

.gallery-title {
  padding-top: 40px;
  font-size: 24px;
}

.secret-container {
  margin-top: 50px;
}

.custom-masonry {
  display: flex;

  .column {
    flex: 1;
    display: flex;
    flex-direction: column;
    margin-right: 15px;
  }
  .column:last-child {
    margin-right: 0px;
  }
}

.controls {
  padding: 20px;
  font-size: 20px;
  text-align: right;
  div {
    display: inline-block;
    margin-left: 15px;
    cursor: pointer;
    transition: transform 0.2s;

    &:hover {
      transform: scale(1.2);
    }
  }
}

.selection-complete-message {
  .buttons {
    margin-top: 30px;

    .button {
      display: inline-block;
      padding: 20px 30px;
      margin: 10px;
      width: 150px;
      font-weight: 500;
      cursor: pointer;
      transition: 0.2s all;

      &:hover {
        transform: scale(1.1);
        @include shadow;
      }
    }
    .continue {
      border: 1px solid $primary;
      border-radius: 5px;
      color: $primary;
    }
    .complete {
      background-color: $secondary;
      color: white;
      border-radius: 5px;
    }
  }
}

.slide-fade-enter-active {
  transition: all 1s ease-out;
}
.slide-fade-enter {
  transform: translateY(50px);
  opacity: 0;
}
</style>